import { createPortal } from 'react-dom';
import { Bell, CheckCheck, AlertTriangle, Info, CheckCircle2, XCircle, X, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useNotifications, type Notification } from '../../hooks/useNotifications';
import { notificationRoute } from '../../lib/notificationRoutes';

const NotificationIcon = ({ type }: { type: Notification['type'] }) => {
    switch (type) {
        case 'warning':
            return <AlertTriangle className="w-4 h-4 text-gold" />;
        case 'success':
            return <CheckCircle2 className="w-4 h-4 text-success" />;
        case 'error':
            return <XCircle className="w-4 h-4 text-danger" />;
        default:
            return <Info className="w-4 h-4 text-info" />;
    }
};

const formatTimeAgo = (dateStr: string): string => {
    const date = new Date(dateStr);
    const diffMs = Date.now() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

/**
 * The notifications list itself, rendered as a centred overlay via a portal.
 *
 * Split out of NotificationBell so every entry point shows the same panel —
 * the sidebar bell and the mobile bottom-bar bell included. Tapping a bell
 * should always show notifications, never navigate away to settings.
 */
export const NotificationPanel = ({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => {
    const navigate = useNavigate();
    const {
        notifications,
        unreadCount,
        loading,
        markAsRead,
        markAllAsRead,
        deleteNotification,
        clearAll,
    } = useNotifications();

    if (!isOpen) return null;

    const handleNotificationClick = async (notification: Notification) => {
        if (!notification.is_read) await markAsRead(notification.id);

        // Navigate to the screen that actions this notification (explicit
        // action_url, or one derived from the notification kind).
        const route = notificationRoute(notification.metadata);
        if (route) {
            navigate(route);
            onClose();
        }
    };

    return createPortal(
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-scrim/50 backdrop-blur-sm animate-in fade-in duration-200"
                onClick={onClose}
            />

            <div className="relative w-full max-w-md max-h-full flex flex-col bg-panel border border-line rounded-[var(--fds-radius)] shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200">
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-line bg-raised shrink-0">
                    <h3 className="font-bold text-fg flex items-center gap-2">
                        <Bell className="w-4 h-4" />
                        Notifications
                    </h3>
                    <div className="flex items-center gap-2">
                        {unreadCount > 0 && (
                            <button
                                onClick={markAllAsRead}
                                className="text-xs text-accent hover:underline flex items-center gap-1"
                            >
                                <CheckCheck className="w-3 h-3" />
                                Mark all read
                            </button>
                        )}
                        {notifications.length > 0 && (
                            <button
                                onClick={clearAll}
                                className="text-xs text-fg-2 hover:text-danger hover:underline flex items-center gap-1 transition-colors"
                                title="Delete all notifications"
                            >
                                <Trash2 className="w-3 h-3" />
                                Clear all
                            </button>
                        )}
                        <button
                            onClick={onClose}
                            aria-label="Close notifications"
                            className="p-1 hover:bg-raised rounded transition-colors"
                        >
                            <X className="w-4 h-4 text-fg-2" />
                        </button>
                    </div>
                </div>

                {/* List */}
                <div className="flex-1 overflow-y-auto">
                    {loading ? (
                        <div className="flex items-center justify-center py-8">
                            <div className="w-6 h-6 border-2 border-accent border-t-transparent rounded-full animate-spin" />
                        </div>
                    ) : notifications.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-12 text-fg-2">
                            <Bell className="w-10 h-10 opacity-30 mb-3" />
                            <p className="text-sm">No notifications yet</p>
                            <p className="text-xs opacity-60 mt-1">You're all caught up!</p>
                        </div>
                    ) : (
                        <div className="divide-y divide-line">
                            {notifications.map((notification) => (
                                <div
                                    key={notification.id}
                                    className={`group relative flex items-stretch hover:bg-raised transition-colors ${
                                        !notification.is_read ? 'bg-accent/5' : ''
                                    }`}
                                >
                                    <button
                                        onClick={() => handleNotificationClick(notification)}
                                        className="flex-1 min-w-0 text-left px-4 py-3"
                                    >
                                        <div className="flex gap-3">
                                            <div className="flex-shrink-0 mt-0.5">
                                                <NotificationIcon type={notification.type} />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-start justify-between gap-2">
                                                    <p
                                                        className={`text-sm font-medium truncate ${
                                                            notification.is_read
                                                                ? 'text-fg-2'
                                                                : 'text-fg'
                                                        }`}
                                                    >
                                                        {notification.title}
                                                    </p>
                                                    {!notification.is_read && (
                                                        <span className="flex-shrink-0 w-2 h-2 mt-1.5 bg-accent rounded-full" />
                                                    )}
                                                </div>
                                                <p className="text-xs text-fg-2 mt-0.5 line-clamp-2">
                                                    {notification.message}
                                                </p>
                                                <p className="text-[10px] text-fg-2 opacity-60 mt-1">
                                                    {formatTimeAgo(notification.created_at)}
                                                </p>
                                            </div>
                                        </div>
                                    </button>

                                    {/* Delete — fades in on hover, and stays put on touch, where
                                        there is no hover to fade it in with. It used to claim that
                                        and not do it: `opacity-0` held on a phone while the
                                        `group-hover` half sat behind `@media (hover: hover)`, so
                                        this was an invisible delete button that still took taps. */}
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            deleteNotification(notification.id);
                                        }}
                                        title="Delete notification"
                                        aria-label="Delete notification"
                                        className="flex-shrink-0 px-3 flex items-center text-fg-2 fd-hover-reveal hover:text-danger"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Footer */}
                {notifications.length > 0 && (
                    <div className="px-4 py-2 border-t border-line bg-raised shrink-0">
                        <button
                            onClick={() => {
                                navigate('/notifications');
                                onClose();
                            }}
                            className="w-full text-center text-xs text-fg-2 hover:text-accent transition-colors"
                        >
                            View all notifications
                        </button>
                    </div>
                )}
            </div>
        </div>,
        document.body,
    );
};
